'use client';

import dynamic from 'next/dynamic';
import React from 'react';
import { ComponentSection } from '@/app/_components/core/component-section';
import { SectionIntro } from '@/app/_components/core/section-intro';
import { getItemTrackId, getSectionProps, getTrackingPrefix } from '@/app/_components/shared/utils/component-helpers';
import { useMediaQuery } from '@/app/_hooks/use-media-query';
import { cn } from '@/app/_lib/utils';
import { CardItem } from './card-item';
import {
  getGridColumns,
  getCardItemClass,
  shouldUseMobileCarousel,
  shouldHavePeek,
} from './card-section-helpers';
import { CardSectionComponentProps } from './card-section.types';

// Mobile carousel (pulls embla) only renders on small viewports for carousel
// card types. Load it lazily so grid / non-carousel card types ship no embla.
const CardSectionCarousel = dynamic(() =>
  import('./card-section-carousel').then((m) => m.CardSectionCarousel)
);

export default function CardSectionComponent({
  component,
  componentPosition,
  isNested,
}: CardSectionComponentProps) {
  const { intro, cardType, cardContent } = component;
  const trackingPrefix = getTrackingPrefix(component);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const cards = cardContent;
  const useMobileCarousel = shouldUseMobileCarousel(cardType);
  const canScroll = cards.length > 1;
  const hasPeek =
    shouldHavePeek(cardType) && canScroll && useMobileCarousel && !isDesktop;
  const showMobileDots = useMobileCarousel && canScroll;

  // JS decides + CSS as backup (prevents flash during hydration)
  const showMobileCarousel = useMobileCarousel && !isDesktop;
  const showGrid = !useMobileCarousel || isDesktop;

  return (
    <ComponentSection isNested={isNested}
      {...getSectionProps(component, componentPosition, isNested)}
      className={cn(
        'container mx-auto flex flex-col py-section-padding-y-mobile xl:py-section-padding-y-desktop',
        hasPeek && 'pr-0 sm:pr-4 xl:pr-6'
      )}
    >
      {intro &&
        (hasPeek ? (
          <div className="pr-4 sm:pr-0">
            <SectionIntro
              intro={intro}
              withMoreLink
              trackingPrefix={trackingPrefix}
            />
          </div>
        ) : (
          <SectionIntro
            intro={intro}
            withMoreLink
            trackingPrefix={trackingPrefix}
          />
        ))}

      {cards.length > 0 && (
        <>
          {/* Mobile: Carousel - JS + CSS backup */}
          {showMobileCarousel && (
            <CardSectionCarousel
              cards={cards}
              cardType={cardType}
              canScroll={canScroll}
              showDots={showMobileDots}
              trackingPrefix={trackingPrefix}
            />
          )}

          {/* Desktop: Grid - JS + CSS backup */}
          {showGrid && (
            <div
              className={cn(
                useMobileCarousel ? 'hidden md:grid' : 'grid',
                getGridColumns(cardType)
              )}
            >
              {cards.map((card, index) => (
                <CardItem
                  key={card.id || index}
                  card={card}
                  cardType={cardType}
                  className={getCardItemClass(cardType, cards.length)}
                  trackId={
                    trackingPrefix
                      ? `${trackingPrefix}-card-${getItemTrackId(card.id, index)}`
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </>
      )}
    </ComponentSection>
  );
}

export { CardSectionComponent };
